import type { ApolloClient } from "@apollo/client";
import {
  createStore,
  type ShellHandle,
  type User,
} from "@observe-tech/mfe-shell-api";
import { createAuthApi } from "./auth";
import { createSitesApi } from "./sites";
import { createPermissionsApi } from "./permissions";
import { createCagesApi } from "./cages";
import { createNavApi } from "./nav";
import { createI18nApi } from "./i18n";
import { createNotify } from "./notify";
import { createCloudSitesApi, type CloudSitesApi } from "./cloudSites";
import { createDateRangeApi } from "./dateRange";
import { createPageMetaApi } from "./pageMeta";
import { createMfeErrorApi, type MfeErrorApi } from "./mfeError";

export interface ShellHandleHost {
  handle: ShellHandle & { cloudSites: CloudSitesApi };
  loadInitialData(): Promise<void>;
  setCurrentUser(u: User | null): void;
  mfeError: MfeErrorApi;
}

export function createShellHandleHost(
  client: ApolloClient<unknown>,
  cloudClient: ApolloClient<unknown>,
): ShellHandleHost {
  const auth = createAuthApi();
  const userStore = createStore<User | null>(null);
  const sites = createSitesApi();
  const permissions = createPermissionsApi(client);
  const cages = createCagesApi(client);
  const nav = createNavApi();
  const i18n = createI18nApi();
  const notify = createNotify();
  const cloudSites = createCloudSitesApi(cloudClient);
  const dateRange = createDateRangeApi();
  const pageMeta = createPageMetaApi();
  const mfeError = createMfeErrorApi();

  const handle: ShellHandle & { cloudSites: CloudSitesApi } = {
    auth: {
      getToken: () => auth.getToken(),
      onTokenRefresh: (cb) => auth.onTokenRefresh(cb),
      currentUser: userStore.store,
    },
    permissions: {
      has: permissions.has,
      list: permissions.list,
      raw: permissions.raw,
      onChange: permissions.onChange,
    },
    sites: {
      list: sites.list,
      selectedSiteId: sites.selectedSiteId,
      setSelectedSite: sites.setSelectedSite,
      refresh: sites.refresh,
      selectedSiteIds: sites.selectedSiteIds,
      setSelectedSiteIds: sites.setSelectedSiteIds,
      selectedCageIds: sites.selectedCageIds,
      setSelectedCageIds: sites.setSelectedCageIds,
    },
    dateRange: {
      range: dateRange.range,
      setRange: dateRange.setRange,
    },
    pageMeta: {
      meta: pageMeta.meta,
      set: pageMeta.set,
      clear: pageMeta.clear,
    },
    cages: {
      listForSite: cages.listForSite,
      refresh: cages.refresh,
    },
    nav,
    i18n,
    notify,
    mode: "shell",
    cloudSites,
  };

  return {
    handle,
    mfeError,
    setCurrentUser: (u) => userStore.set(u),
    loadInitialData: async () => {
      try {
        const u = await auth.loadCurrentUser();
        userStore.set(u);
      } catch {
        userStore.set(null);
      }
      await permissions.refresh();
      const ids = permissions.raw().flatMap((p) => p.sites);
      sites.setFromIds(ids);
      await cloudSites.refresh();
    },
  };
}
